import { SelectIcon } from '@/assets/icons/selectIcon';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import { ErrorText } from '@/components/basicComponents/input';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { Loader } from '@/components/loaders/loader';
import { useClickOutside } from '@/hooks/useClickOutside';
import { themeVar } from '@/styles/theme';
import { AnimatePresence, motion } from 'framer-motion';
import { debounce } from 'lodash';
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Label from '../label';

export interface ISelectValue {
  label: string;
  value: string;
  tooltip?: React.ReactNode;
  tooltipWidth?: number;
}

interface ISelectValueWithActive extends ISelectValue {
  isActive?: boolean;
}

interface SelectProps {
  value?: string;
  setValue?: (value: string) => void;
  saveSearchResult?: (value: string) => void;
  options?: ISelectValue[];
  size?: 'sm' | 'md' | 'lg';
  styleType?: 'default' | 'form' | 'success' | 'error' | 'disabled';
  width?: string;
  loading?: boolean;
  description?: string;
  errorText?: string;
  label?: string;
  tooltip?: string;
  tooltipWidth?: number;
  isRequiredField?: boolean;
  defaultSelectLabel?: string;
  previewValue?: string;
  withSearch?: boolean;
  centerList?: boolean;
  className?: string;
  customArrowIcon?: React.ReactNode;
  additionalOption?: React.ReactNode;
  'data-testid'?: string;
  'data-testid-dropdown'?: string;
  'data-testid-option'?: string;
  'data-testid-label'?: string;
  'data-testid-error'?: string;
}

const getSelectSize = (size: SelectProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        padding: 8px 5px 8px 5px;
        border-radius: 3px;
      `;
    case 'md':
      return css`
        padding: 10px;
        border-radius: 4px;
      `;
    case 'lg':
      return css`
        padding: 10px 12px 10px 12px;
        border-radius: 5px;
      `;
    default:
      return '';
  }
};

const getSelectStyleType = (styleType: SelectProps['styleType'], isDefaultSelectValue: boolean) => {
  switch (styleType) {
    case 'default':
      return css`
        background-color: ${themeVar('layer.1')};
        border: 1px solid ${themeVar('border.base')};
        color: ${isDefaultSelectValue ? themeVar('font.weak') : themeVar('font.strong')};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${themeVar('layer.1')} inset;
          -webkit-text-fill-color: ${themeVar('font.strong')};
        }
      `;
    case 'form':
      return css`
        background-color: ${themeVar('layer.2')};
        border: 1px solid ${themeVar('border.base')};
        color: ${isDefaultSelectValue ? themeVar('font.weak') : themeVar('font.strong')};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${themeVar('layer.2')} inset;
          -webkit-text-fill-color: ${themeVar('font.strong')};
        }
      `;
    case 'success':
      return css`
        background-color: #efffea;
        border: 1px solid ${themeVar('context.success')};
        color: ${isDefaultSelectValue ? themeVar('font.weak') : '#2e2e2e'};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #efffea inset;
          -webkit-text-fill-color: #2e2e2e;
        }
      `;
    case 'error':
      return css`
        background-color: #ffeaec;
        border: 1px solid ${themeVar('context.error')};
        color: ${isDefaultSelectValue ? themeVar('font.weak') : '#2e2e2e'};

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #ffeaec inset;
          -webkit-text-fill-color: #2e2e2e;
        }
      `;
    case 'disabled':
      return css`
        background-color: ${themeVar('action.disabled')};
        border: 1px solid ${themeVar('border.base')};
        color: ${themeVar('font.disabled')};
      `;
    default:
      return '';
  }
};

const getFontProperties = (size: SelectProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        font-size: 13px;
        line-height: 18.2px;
      `;
    case 'md':
      return css`
        font-size: 16px;
        line-height: 22.4px;
      `;
    case 'lg':
      return css`
        font-size: 19px;
        line-height: 26.6px;
      `;
    default:
      return '';
  }
};

const getSelectListTopPosition = (size: SelectProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        top: 42px;
      `;
    case 'md':
      return css`
        top: 50px;
      `;
    case 'lg':
      return css`
        top: 55px;
      `;
    default:
      return '';
  }
};

const getSearchInputStyleType = (styleType: SelectProps['styleType']) => {
  switch (styleType) {
    case 'default':
      return css`
        background-color: ${themeVar('layer.1')};
        color: ${themeVar('font.strong')};
      `;
    case 'form':
      return css`
        background-color: ${themeVar('layer.2')};
        color: ${themeVar('font.strong')};
      `;
    case 'success':
      return css`
        background-color: #efffea;
        color: '#2e2e2e';
      `;
    case 'error':
      return css`
        background-color: #ffeaec;
        color: '#2e2e2e';
      `;
    case 'disabled':
      return css`
        background-color: ${themeVar('action.disabled')};
        color: ${themeVar('font.disabled')};
      `;
    default:
      return '';
  }
};

const Select = ({
  value,
  setValue,
  saveSearchResult,
  options,
  size = 'lg',
  styleType = 'default',
  width = '100%',
  loading = false,
  description,
  errorText,
  label,
  tooltip,
  tooltipWidth = 0,
  isRequiredField = false,
  defaultSelectLabel = 'Select',
  previewValue,
  className,
  withSearch = false,
  centerList = false,
  customArrowIcon,
  additionalOption,
  'data-testid': testId,
  'data-testid-dropdown': dropdownTestId,
  'data-testid-option': optionTestId,
  'data-testid-label': labelTestId,
  'data-testid-error': errorTestId
}: SelectProps) => {
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const selectItemsRef = useRef<HTMLDivElement | null>(null);

  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState(defaultSelectLabel);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [filteredSelectOptions, setFilteredSelectOptions] = useState<ISelectValueWithActive[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);

  const selectRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(selectRef, () => {
    setIsSelectOpen(false);
    if (!value) {
      setSearch('');
      setIsSearching(false);
      setFilteredSelectOptions(options || []);
      setHighlightedIndex(-1);
    }
  });

  useEffect(() => {
    if (isSearching) return;
    // if (!value) {
    //   setSelectedLabel(defaultSelectLabel);
    //   return;
    // }

    const selectedOption = options?.find((option) => option.value === value);
    const label = selectedOption ? selectedOption.label : previewValue;
    const newHighlightedIndex = options?.findIndex((option) => option.value === value);
    setSelectedLabel(label || defaultSelectLabel);
    setHighlightedIndex(newHighlightedIndex);
    setFilteredSelectOptions(
      options?.map((item, index) => ({
        ...item,
        isActive: index === newHighlightedIndex
      })) || []
    );
    if (withSearch) {
      setSearch(label !== defaultSelectLabel ? label || '' : '');
    }
  }, [value, options]);

  useEffect(() => {
    if (!saveSearchResult) return;
    saveSearchResult(search);
  }, [search]);

  const handleSelectClick = () => {
    if (styleType === 'disabled') return;
    setIsSelectOpen(!isSelectOpen);
    if (!value) {
      setHighlightedIndex(-1);
    }
    if (!isSelectOpen) {
      setTimeout(() => {
        scrollToActiveItem();
      }, 0);
    }
  };

  const matchSearchValueWithOptions = debounce((searchValue: string, filteredList: ISelectValue[]) => {
    const selectedOption = filteredList.find((item) => item.label.toLowerCase() === searchValue.toLowerCase());

    if (selectedOption?.value) {
      setSearch(selectedOption?.label);
      setValue?.(selectedOption?.value);
      setIsSelectOpen(false);
      setIsSearching(false);
    } else {
      setValue?.('');
    }
  }, 300);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    setIsSearching(true);
    setIsSelectOpen(true);
    setHighlightedIndex(-1);
    const selectOptionsCopy = [...(options || [])];
    const filteredList = selectOptionsCopy.filter((item) => item.label.toLowerCase().indexOf(event.target.value.toLowerCase()) !== -1);
    matchSearchValueWithOptions(value, filteredList);

    if (value === '' && filteredList.length === 0) {
      setFilteredSelectOptions(options || []);
    } else {
      setFilteredSelectOptions(filteredList);
    }
  };

  const scrollToOption = (index: number) => {
    const optionElement = optionRefs.current[index];
    if (optionElement) {
      optionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  };

  const scrollToActiveItem = () => {
    if (highlightedIndex >= 0 && selectItemsRef.current) {
      const optionElement = optionRefs.current[highlightedIndex];
      const containerElement = selectItemsRef.current;

      if (optionElement && containerElement) {
        const scrollTop = optionElement.offsetTop - containerElement.clientHeight / 2 + optionElement.clientHeight / 2;
        containerElement.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'auto'
        });
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
    if (event.key === 'Tab') {
      setIsSelectOpen(false);
      if (!value) {
        setSearch('');
        setFilteredSelectOptions(options || []);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsSelectOpen(false);
      if (!value) {
        setSearch('');
        setIsSearching(false);
        setFilteredSelectOptions(options || []);
        setHighlightedIndex(-1);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      if (!isSelectOpen) {
        if (!options?.length) return;
        setIsSelectOpen(true);
        const lastIndex = options.length - 1;
        const newIndex = lastIndex;
        setHighlightedIndex(newIndex);
        setFilteredSelectOptions(
          options.map((item, index) => ({ ...item, isActive: index === newIndex }))
        );
        setTimeout(() => scrollToOption(newIndex), 0);
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : 0;
      setFilteredSelectOptions((prevFilteredSelectOptions) =>
        prevFilteredSelectOptions.map((item, index) => ({
          ...item,
          isActive: index === newIndex
        }))
      );
      scrollToOption(newIndex);
      setHighlightedIndex(newIndex);
      return;
    }

    if (event.key === 'ArrowDown') {
      if (!isSelectOpen) {
        if (!options?.length) return;
        setIsSelectOpen(true);
        setTimeout(() => {
          scrollToActiveItem();
        }, 0);
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const newIndex =
        highlightedIndex < filteredSelectOptions.length - 1
          ? highlightedIndex + 1
          : filteredSelectOptions.length - 1;
      setFilteredSelectOptions((prevFilteredSelectOptions) =>
        prevFilteredSelectOptions.map((item, index) => ({
          ...item,
          isActive: index === newIndex
        }))
      );
      scrollToOption(newIndex);
      setHighlightedIndex(newIndex);
      return;
    }

    if (event.key === 'Enter') {
      if (isSelectOpen) {
        event.preventDefault();
        if (highlightedIndex >= 0) {
          const selectedOption = filteredSelectOptions[highlightedIndex];
          if (selectedOption) {
            setValue?.(selectedOption.value);
            setSearch(selectedOption.label);
            setIsSelectOpen(false);
          }
        }
      }
    }
  };

  return (
    <SelectContainer width={width}>
      {label && (
        <Label required={isRequiredField} size={size} tooltip={tooltip} tooltipWidth={tooltipWidth} data-testid={labelTestId}>
          {label}
        </Label>
      )}
      <SelectWrapper
        data-testid={testId}
        styleType={styleType}
        size={size}
        isDefaultSelectValue={selectedLabel === defaultSelectLabel}
        className={className}
        onClick={handleSelectClick}
        onKeyDown={handleKeyDown}
        ref={selectRef}
        tabIndex={withSearch ? undefined : 0}
      >
        {withSearch ? (
          <>
            <SearchInput
              data-testid={testId ? `${testId}.input` : undefined}
              id="input"
              placeholder={defaultSelectLabel}
              value={search}
              onChange={onChange}
              onKeyDown={handleKeyDown}
              size={size}
              disabled={styleType === 'disabled'}
              styleType={styleType}
              autoComplete="off"
            />
            <SelectIconWrap isOpen={isSelectOpen}>
              {customArrowIcon ?? <SelectIcon fill={styleType === 'disabled' ? themeVar('font.disabled') : themeVar('font.action')} />}
            </SelectIconWrap>
          </>
        ) : (
          <>
            <EllipsisWithTooltip title={selectedLabel} maxContentWidth={300}>
              <FilterTitle id="titleSelect">{selectedLabel}</FilterTitle>
            </EllipsisWithTooltip>
            <SelectIconWrap isOpen={isSelectOpen}>
              {customArrowIcon ?? <SelectIcon fill={styleType === 'disabled' ? themeVar('font.disabled') : themeVar('font.action')} />}
            </SelectIconWrap>
          </>
        )}
        <AnimatePresence>
          {isSelectOpen && (
            <SelectItems
              data-testid={dropdownTestId}
              id="dropdownSelect"
              ref={selectItemsRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              width={width}
              size={size}
              $centerList={centerList}
              tabIndex={-1}
            >
              {loading ? (
                <Loader size={20} />
              ) : (
                <>
                  {filteredSelectOptions.length ? (
                    <>
                      {filteredSelectOptions?.map((item, optionIndex) => (
                        <Tooltip key={item.value} title={item?.tooltip} maxContentWidth={item?.tooltipWidth} disableTooltip={!item?.tooltip}>
                          <SelectOption
                            data-testid={optionTestId}
                            data-value={item.value}
                            size={size}
                            isActive={item.isActive}
                            onClick={() => {
                              setSearch(item.value ? item.label : '');
                              setValue?.(item.value);
                              setHighlightedIndex(optionIndex);
                              setFilteredSelectOptions((prevFilteredSelectOptions) =>
                                prevFilteredSelectOptions.map((item, index) => ({
                                  ...item,
                                  isActive: optionIndex === index
                                }))
                              );
                            }}
                            isDefaultSelectValue={defaultSelectLabel === item.label}
                            ref={(el) => (optionRefs.current[optionIndex] = el)}
                          >
                            {item.label}
                          </SelectOption>
                        </Tooltip>
                      ))}
                    </>
                  ) : (
                    <NoResults size={size}>No Results</NoResults>
                  )}
                </>
              )}
              {additionalOption && isSelectOpen && (
                <AdditionalOptionWrapper>
                  <AdditionalOption>{additionalOption}</AdditionalOption>
                </AdditionalOptionWrapper>
              )}
            </SelectItems>
          )}
        </AnimatePresence>
      </SelectWrapper>
      {description && <Description>{description}</Description>}
      {errorText && <ErrorText size={size} data-testid={errorTestId}>{errorText}</ErrorText>}
    </SelectContainer>
  );
};

export default Select;

const SelectContainer = styled.div<{ width: string }>`
  width: ${({ width }) => width};
`;

const SelectWrapper = styled.div<{ styleType: SelectProps['styleType']; size: SelectProps['size']; isDefaultSelectValue: boolean }>`
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  cursor: ${({ styleType }) => (styleType === 'disabled' ? 'not-allowed' : 'pointer')};

  ${({ size }) => getSelectSize(size)}
  ${({ styleType, isDefaultSelectValue }) => getSelectStyleType(styleType, isDefaultSelectValue)}
  ${({ size }) => getFontProperties(size)}
`;

const SearchInput = styled.input<{ size: SelectProps['size']; styleType: SelectProps['styleType'] }>`
  font-family: Blinker;
  width: 100%;
  border: none;
  cursor: ${({ styleType }) => (styleType === 'disabled' ? 'not-allowed' : 'auto')};

  &:focus {
    outline: none;
  }

  ${({ styleType }) => getSearchInputStyleType(styleType)}
  ${({ size }) => getFontProperties(size)}
`;

const SelectIconWrap = styled.div<{ isOpen: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  transform-origin: center;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: all 0.2s linear;
`;

const SelectItems = styled(motion.div)<{ width: string; size: SelectProps['size']; $centerList: SelectProps['centerList'] }>`
  max-height: 260px;
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 999;
  ${({ size }) => getSelectListTopPosition(size)}
  ${({ $centerList }) =>
    $centerList
      ? css`
          left: 50%;
          transform: translateX(-50%);
        `
      : css`
          right: 0;
        `};
  width: 100%;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);
  border-radius: 3px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: none;

  &::-webkit-scrollbar {
    width: 10px;
    left: -100px;
  }

  &::-webkit-scrollbar-track {
    background: ${themeVar('layer.2')};
    border: 1px solid ${themeVar('border.base')};
    padding: 2px;
    border-radius: 10px;
    margin: 6px 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${themeVar('layer.4')};
    background-clip: padding-box;
    border-radius: 10px;
  }
`;

const FilterTitle = styled.div`
  width: 100%;
`;

const SelectOption = styled.div<{ size: SelectProps['size']; isDefaultSelectValue: boolean; isActive?: boolean }>`
  color: ${({ isDefaultSelectValue }) => (isDefaultSelectValue ? themeVar('font.weak') : themeVar('font.strong'))};
  text-align: left;
  width: 100%;
  cursor: pointer;
  background: ${({ isActive }) => (isActive ? themeVar('layer.12') : themeVar('layer.1'))};

  &:hover {
    background: ${themeVar('layer.12')};
  }

  ${({ size }) => getSelectSize(size)}
`;

const NoResults = styled.div<{ size: SelectProps['size'] }>`
  color: ${themeVar('font.strong')};
  text-align: center;
  width: 100%;
  white-space: nowrap;
  background: ${themeVar('layer.1')};

  ${({ size }) => getSelectSize(size)}
`;

const AdditionalOptionWrapper = styled.div`
  position: sticky;
  bottom: 0;
  background-color: ${themeVar('layer.1')};
`;

const AdditionalOption = styled.div`
  padding: 10px 0;
  margin: 0 10px;
  border-top: 1px solid ${themeVar('border.base')};
`;

const Description = styled.span`
  font-weight: 300;
  font-size: 13px;
  line-height: 18.2px;
  color: ${themeVar('font.weak')};
`;
